/**
 * Category Page Component - AJAX Filtering, Sorting, View Switching
 *
 * server-Compatible Architecture:
 * - Server handles all data operations (filtering, sorting, pagination)
 * - AJAX requests fetch updated HTML from server
 * - Client-side only updates DOM and manages URL history
 * - Preserves all URL parameters for proper back/forward navigation
 *
 * Based on default.catalog successful implementation
 */
export default function categoryPage() {
  return {
    // ========================================
    // STATE
    // ========================================
    loading: false,
    viewMode: new URLSearchParams(window.location.search).get('list_style') || 'columns',
    gridCols: new URLSearchParams(window.location.search).get('grid_cols') || '3',
    sortBy: new URLSearchParams(window.location.search).get('sort') || 'popularity',
    sortOpen: false,
    abortController: null, // Track current AJAX request controller

    // ========================================
    // INITIALIZATION
    // ========================================
    init() {
      // Wait for DOM to be fully ready, then setup AJAX filtering
      this.$nextTick(() => {
        this.setupAjaxFiltering();
      });

      // Handle browser back/forward buttons
      window.addEventListener('popstate', e => {
        if (e.state && e.state.categoryPage) {
          // Reload page on back/forward to ensure proper state
          window.location.reload();
        }
      });

      // Mark initial state for history
      if (window.history.state === null) {
        window.history.replaceState(
          { categoryPage: true, url: window.location.href },
          '',
          window.location.href
        );
      }

      // Handle dispatched events from child components
      this.$el.addEventListener('update-view', e => {
        this.updateView(e.detail);
      });

      this.$el.addEventListener('update-sort', e => {
        this.updateSort(e.detail);
      });

      this.$el.addEventListener('update-grid-cols', e => {
        this.updateGridCols(e.detail);
      });

      this.$el.addEventListener('toggle-filter-sidebar', () => {
        this.toggleFilterSidebar();
      });

      // Initialize filter tokens
      setTimeout(() => {
        this.updateFilterTokens();
      }, 200);
    },

    // ========================================
    // AJAX FILTERING SETUP (Based on default.catalog pattern)
    // ========================================
    setupAjaxFiltering() {
      // Find the filter form - use the same selector as default theme
      const filterForm = document.querySelector('form[name="filter_form"]');

      if (!filterForm) {
        setTimeout(() => this.setupAjaxFiltering(), 100);
        return;
      }

      // Trigger initial token update for search input
      const searchInput = filterForm.querySelector('input[name="product_name"]');
      if (searchInput) {
        // Trigger input event to initialize tokens (like default theme)
        searchInput.dispatchEvent(new Event('input'));
      }

      // Setup AJAX filtering on form input changes (exactly like default theme)
      filterForm.addEventListener('input', e => {
        this.handleFilterInput();
      });

      // Intercept pagination links
      this.interceptPaginationLinks();
    },

    // ========================================
    // FILTER OPERATIONS (Based on default.catalog pattern)
    // ========================================
    handleFilterInput() {
      // Abort previous request if still running
      if (this.abortController) {
        this.abortController.abort();
      }

      // Create new AbortController for this request
      this.abortController = new AbortController();

      const filterForm = document.querySelector('form[name="filter_form"]');
      if (!filterForm) return;

      // Build URL with form data (same as default theme)
      const formData = new FormData(filterForm);
      const params = new URLSearchParams();

      // Convert FormData to URLSearchParams
      for (const [key, value] of formData.entries()) {
        params.append(key, value);
      }

      const url = new URL(
        location.protocol + '//' + location.host + location.pathname + '?' + params.toString()
      );

      // Update browser history
      history.replaceState({ categoryPage: true }, null, url);

      // Hide current product listing
      const productListing = document.querySelector('.product-listing');
      if (productListing) {
        productListing.style.opacity = '0.5';
      }

      // Show loading state
      this.showLoading();

      // Make AJAX request with AbortController signal
      fetch(url.href, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        signal: this.abortController.signal,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(html => {
          this.updatePageContent(html);
          this.hideLoading();
          // Clear the controller since request completed successfully
          this.abortController = null;
        })
        .catch(error => {
          // Don't handle aborted requests as errors
          if (error.name === 'AbortError') {
            return;
          }

          this.hideLoading();
          // Clear the controller on error
          this.abortController = null;
          // Fallback to full page reload on error
          window.location.href = url.href;
        });
    },

    // ========================================
    // DOM UPDATE (Based on default.catalog pattern)
    // ========================================
    updatePageContent(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product listing (fashion.catalog uses .product-listing)
      const newProductListing = doc.querySelector('.product-listing');
      const currentProductListing = document.querySelector('.product-listing');

      if (newProductListing && currentProductListing) {
        // Replace product listing content
        currentProductListing.innerHTML = newProductListing.innerHTML;
        currentProductListing.className = newProductListing.className;
        currentProductListing.style.opacity = '1';
      }

      // Update pagination (fashion.catalog uses .pagination-nav)
      const newPagination = doc.querySelector('.pagination-nav');
      const currentPagination = document.querySelector('.pagination-nav');

      // Remove old pagination
      if (currentPagination) {
        currentPagination.remove();
      }

      // Add new pagination if exists
      if (newPagination && currentProductListing) {
        currentProductListing.after(newPagination);
        // Re-intercept pagination links
        this.interceptPaginationLinks();
      }

      // Update filter tokens
      this.updateFilterTokens();

      // Re-initialize Alpine components in updated content
      if (window.Alpine) {
        const productListing = document.querySelector('.product-listing');
        if (productListing) {
          window.Alpine.initTree(productListing);
        }
      }
    },

    // ========================================
    // TOKEN MANAGEMENT (Based on default.catalog pattern)
    // ========================================
    updateFilterTokens() {
      const filterForm = document.querySelector('form[name="filter_form"]');
      if (!filterForm) return;

      // Find or create tokens container
      let tokenContainer = document.querySelector('.filter-tokens');
      if (!tokenContainer) {
        // Create tokens container if it doesn't exist
        const filtersHeader = document.querySelector('#filters-heading');
        if (filtersHeader && filtersHeader.parentNode) {
          tokenContainer = document.createElement('div');
          tokenContainer.className = 'filter-tokens mb-4';
          filtersHeader.parentNode.insertBefore(tokenContainer, filtersHeader.nextSibling);
        }
      }

      if (!tokenContainer) return;

      // Clear existing tokens
      tokenContainer.innerHTML = '';

      // Add search tokens
      const searchInputs = filterForm.querySelectorAll('input[data-token-title][type="search"]');
      searchInputs.forEach(input => {
        if (input.value.trim()) {
          const token = this.createToken(
            input.getAttribute('data-token-group'),
            input.name,
            input.value,
            input.getAttribute('data-token-title') + ': ' + input.value
          );
          tokenContainer.appendChild(token);
        }
      });

      // Add checkbox/radio tokens
      const checkedInputs = filterForm.querySelectorAll(
        'input[data-token-title][type="checkbox"]:checked, input[data-token-title][type="radio"]:checked'
      );
      checkedInputs.forEach(input => {
        if (input.value) {
          const token = this.createToken(
            input.getAttribute('data-token-group'),
            input.name,
            input.value,
            input.getAttribute('data-token-title') + ': ' + input.getAttribute('data-token-value')
          );
          tokenContainer.appendChild(token);
        }
      });
    },

    createToken(group, name, value, text) {
      const token = document.createElement('span');
      token.className =
        'inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-sm mr-2 mb-2';
      token.setAttribute('data-group', group);
      token.setAttribute('data-name', name);
      token.setAttribute('data-value', value);

      // Create token content safely without template literals for SVG
      const textSpan = document.createElement('span');
      textSpan.textContent = text;

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'text-accent hover:text-accent/70 transition-colors remove-token';
      removeButton.setAttribute('aria-label', window.tx('removeFilter', 'Remove filter'));

      // Get icon sprite path from template
      const existingIcon = document.querySelector('use[href*="icons-sprite.svg"]');
      const iconSpritePath = existingIcon ? existingIcon.getAttribute('href').split('#')[0] : '';
      const iconHref = iconSpritePath ? iconSpritePath + '#icon-x' : '#icon-x';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'w-4 h-4');

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', iconHref);
      use.setAttribute('href', iconHref);

      svg.appendChild(use);
      removeButton.appendChild(svg);

      token.appendChild(textSpan);
      token.appendChild(removeButton);

      // Add remove functionality (same as default theme)
      const removeBtn = token.querySelector('.remove-token');
      removeBtn.addEventListener('click', e => {
        e.preventDefault();
        this.removeToken(token);
      });

      return token;
    },

    removeToken(token) {
      const name = token.getAttribute('data-name');
      const value = token.getAttribute('data-value');

      // Try to find input with exact name match first
      let input = document.querySelector(`input[name="${name}"][value="${value}"]`);

      // If not found, try with escaped name (for array inputs like manufacturers[], attributes[1][])
      if (!input) {
        const escapedName = name.replace(/\[/g, '\\[').replace(/\]/g, '\\]');
        input = document.querySelector(`input[name="${escapedName}"][value="${value}"]`);
      }

      // If still not found, try finding by value attribute and checking parent form
      if (!input) {
        const filterForm = document.querySelector('form[name="filter_form"]');
        if (filterForm) {
          const inputs = filterForm.querySelectorAll(`input[value="${value}"]`);
          for (const inp of inputs) {
            if (inp.name === name) {
              input = inp;
              break;
            }
          }
        }
      }

      if (input) {
        switch (input.type) {
          case 'radio':
          case 'checkbox':
            input.checked = false;
            break;
          case 'text':
          case 'search':
            input.value = '';
            break;
        }

        // Remove token from DOM immediately
        token.remove();

        // Trigger input event to update filters via AJAX
        input.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        token.remove();
      }
    },

    // ========================================
    // VIEW OPERATIONS - AJAX (No Page Reload)
    // ========================================
    async updateView(view) {
      this.viewMode = view;
      const url = new URL(window.location);
      url.searchParams.set('list_style', view);
      url.searchParams.delete('page'); // Reset to page 1

      // Update URL without reload
      history.replaceState({ categoryPage: true }, null, url);

      // Fetch and update content
      this.fetchAndUpdateContent(url);
    },

    async updateGridCols(cols) {
      this.gridCols = cols;
      const url = new URL(window.location);
      url.searchParams.set('grid_cols', cols);
      url.searchParams.delete('page'); // Reset to page 1

      // Update URL without reload
      history.replaceState({ categoryPage: true }, null, url);

      // Fetch and update content
      this.fetchAndUpdateContent(url);
    },

    async updateSort(sortValue) {
      this.sortBy = sortValue;
      this.sortOpen = false;

      const url = new URL(window.location);
      url.searchParams.set('sort', sortValue);
      url.searchParams.delete('page'); // Reset to page 1

      // Update URL without reload
      history.replaceState({ categoryPage: true }, null, url);

      // Fetch and update content
      this.fetchAndUpdateContent(url);
    },

    // ========================================
    // FETCH AND UPDATE CONTENT (AJAX)
    // ========================================
    fetchAndUpdateContent(url) {
      // Abort previous request if still running
      if (this.abortController) {
        this.abortController.abort();
      }

      // Create new AbortController for this request
      this.abortController = new AbortController();

      // Show loading state
      this.showLoading();

      // Make AJAX request
      fetch(url.href, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        signal: this.abortController.signal,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(html => {
          this.updatePageContent(html);
          this.hideLoading();
          this.abortController = null;
        })
        .catch(error => {
          // Don't handle aborted requests as errors
          if (error.name === 'AbortError') {
            return;
          }

          this.hideLoading();
          this.abortController = null;

          // Show error notification
          if (window.Notification) {
            window.Notification.error(window.tx('errorUpdateContent', 'Failed to update content. Please try again.'), 5000);
          }
        });
    },

    // ========================================
    // PAGINATION
    // ========================================
    interceptPaginationLinks() {
      const paginationLinks = document.querySelectorAll('.pagination-nav a');
      paginationLinks.forEach(link => {
        link.addEventListener('click', e => {
          e.preventDefault();
          const url = link.getAttribute('href');
          if (url) {
            // For pagination, do a full page reload to ensure proper state
            window.location.href = url;
          }
        });
      });
    },

    // ========================================
    // CLEAR FILTERS
    // ========================================
    async clearAllFilters() {
      const url = new URL(window.location);

      // Remove filter parameters but keep view settings
      url.searchParams.delete('manufacturers');
      url.searchParams.delete('attributes');
      url.searchParams.delete('price_min');
      url.searchParams.delete('price_max');
      url.searchParams.delete('product_name');
      url.searchParams.delete('page');

      window.location.href = url.href;
    },

    // ========================================
    // UI HELPERS
    // ========================================
    showLoading() {
      this.loading = true;

      // Show global loader
      if (window.Loader) {
        window.Loader.show('category-filter', window.tx('loadingDefault', 'Loading...'));
      }
    },

    hideLoading() {
      this.loading = false;

      // Hide global loader
      if (window.Loader) {
        window.Loader.hide('category-filter');
      }
    },

    toggleFilterSidebar() {
      const sidebar = document.getElementById('filter-sidebar');
      if (sidebar) {
        sidebar.classList.toggle('hidden');
        sidebar.classList.toggle('lg:block');
        document.body.classList.toggle('overflow-hidden');
      }
    },
  };
}
